import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlertTriangle, Undo2, RotateCcw } from 'lucide-react';
import {
  isUndecodedContractInteraction,
  type AccountTransactionChildWithContext,
  type PolicyProposalImpactPreview,
} from '@mls-wallet/shared-types';
import { TokenLogo } from '@/components/shared/token-logo';
import {
  getContractDisplayName,
  getOpaqueContractDisplayName,
} from '@/components/transactions/utils';

function getConfirmButtonLabel(
  isSubmitting: boolean,
  allExcluded: boolean,
  remainingInvalidatedCount: number
): string {
  if (isSubmitting) return 'Saving...';
  if (allExcluded) return 'Confirm & Save Changes';

  const noun = remainingInvalidatedCount === 1 ? 'transaction' : 'transactions';
  return `Confirm & Invalidate ${remainingInvalidatedCount} ${noun}`;
}

function AddressWithTooltip({
  label,
  name,
  address,
}: {
  label: string;
  name: string;
  address: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-xs text-muted-foreground">
            <span className="text-muted-foreground/70">{label}:</span>{' '}
            <span className="font-medium text-foreground">{name}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="font-mono text-xs">{address}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function formatAddress(address: string): string {
  return (
    address.substring(0, 6) + '...' + address.substring(address.length - 4)
  );
}

/**
 * Summarizes an affected transaction, preserving resolved opaque-call names
 * and shortening only an unnamed raw target.
 */
export function PolicyImpactTransactionRow({
  tx,
}: {
  tx: AccountTransactionChildWithContext;
}) {
  const isTokenTransfer = tx.type === 'TOKEN_TRANSFER';
  const isOpaqueContractCall = isUndecodedContractInteraction(tx);
  const opaqueContractDisplayName =
    tx.type === 'CONTRACT_INTERACTION' && isOpaqueContractCall
      ? getOpaqueContractDisplayName(tx)
      : null;
  const opaqueContractSummary =
    opaqueContractDisplayName === tx.toAddress
      ? formatAddress(tx.toAddress)
      : opaqueContractDisplayName;

  return (
    <div className="bg-muted rounded p-3 text-sm">
      <div className="flex justify-between gap-4">
        {/* Left column: Transaction details */}
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          {isTokenTransfer ? (
            <>
              <span className="font-semibold text-foreground">
                Token Transfer
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <TokenLogo
                  symbol={tx.asset.symbol}
                  logoUrl={tx.asset.logoUrl}
                  size="sm"
                />
                {tx.displayAmount} {tx.asset.symbol}
              </span>
            </>
          ) : (
            <>
              <span className="font-semibold text-foreground">
                Contract Call
              </span>
              <span className="text-muted-foreground">
                {isOpaqueContractCall
                  ? opaqueContractSummary
                  : `${getContractDisplayName(tx)} → ${tx.functionName}`}
              </span>
            </>
          )}
        </div>

        {/* Right column: From, To */}
        <div className="flex flex-col gap-0.5 items-end shrink-0">
          <AddressWithTooltip
            label="From"
            name={tx.account.name}
            address={tx.account.address}
          />
          {isTokenTransfer && (
            <AddressWithTooltip
              label="To"
              name={formatAddress(tx.destination)}
              address={tx.destination}
            />
          )}
        </div>
      </div>

      {/* Description row with Initiated date */}
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-muted-foreground truncate flex-1 mr-2">
          {tx.description}
        </span>
        <span className="text-xs text-muted-foreground shrink-0">
          <span className="text-muted-foreground/70">Initiated:</span>{' '}
          {new Date(tx.dateProposed).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

interface PolicyImpactPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  impact: PolicyProposalImpactPreview | null;
  /** Called with the IDs of policy changes to exclude (undo). Only used when allowUndo=true */
  onConfirm: (excludedPolicyChangeIds: string[]) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  /** If true, shows undo toggles for each policy change. Default: true */
  allowUndo?: boolean;
}

export function PolicyImpactPreviewDialog({
  open,
  onOpenChange,
  impact,
  onConfirm,
  onCancel,
  isSubmitting,
  allowUndo = true,
}: PolicyImpactPreviewDialogProps) {
  // Track which policy changes are "undone" (excluded from the proposal)
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  // Reset excluded IDs when dialog opens with new impact data
  useEffect(() => {
    if (open) {
      setExcludedIds(new Set());
    }
  }, [open, impact]);

  if (!impact || impact.totalInvalidatedCount === 0) {
    return null;
  }

  const toggleExcluded = (id: string) => {
    if (!allowUndo) return;
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Calculate how many transactions will still be invalidated after exclusions
  const remainingInvalidatedCount = allowUndo
    ? impact.policyChanges
        .filter((pc) => !excludedIds.has(pc.id))
        .reduce((sum, pc) => sum + pc.affectedTransactions.length, 0)
    : impact.totalInvalidatedCount;

  const allExcluded =
    allowUndo && excludedIds.size === impact.policyChanges.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Pending Transactions Will Be Invalidated
          </DialogTitle>
          <DialogDescription>
            {remainingInvalidatedCount > 0 ? (
              <>
                This policy change will invalidate{' '}
                <strong>{remainingInvalidatedCount}</strong> pending{' '}
                {remainingInvalidatedCount === 1
                  ? 'transaction'
                  : 'transactions'}
                . These transactions would need to be re-created after the
                policy change is applied.
              </>
            ) : (
              <>
                All impacting policy changes have been excluded. No transactions
                will be invalidated.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {impact.policyChanges.map((policyChange) => {
            const isExcluded = excludedIds.has(policyChange.id);

            return (
              <div
                key={policyChange.id}
                className={`border rounded-lg p-4 space-y-3 transition-opacity ${
                  isExcluded ? 'opacity-50 bg-muted/50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <h4
                    className={`font-medium flex-1 ${isExcluded ? 'line-through text-muted-foreground' : ''}`}
                  >
                    {policyChange.policyName}
                  </h4>
                  {allowUndo ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleExcluded(policyChange.id)}
                      className={
                        isExcluded
                          ? 'text-primary border-primary hover:bg-primary/10'
                          : 'text-muted-foreground hover:text-foreground'
                      }
                    >
                      {isExcluded ? (
                        <>
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Restore
                        </>
                      ) : (
                        <>
                          <Undo2 className="h-3.5 w-3.5 mr-1.5" />
                          Undo
                        </>
                      )}
                    </Button>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {policyChange.affectedTransactions.length}{' '}
                      {policyChange.affectedTransactions.length === 1
                        ? 'transaction'
                        : 'transactions'}
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  {policyChange.affectedTransactions.map((tx) => (
                    <PolicyImpactTransactionRow key={tx.id} tx={tx} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Go Back
          </Button>
          <Button
            variant={allExcluded ? 'default' : 'destructive'}
            onClick={() => onConfirm(Array.from(excludedIds))}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {getConfirmButtonLabel(
              isSubmitting,
              allExcluded,
              remainingInvalidatedCount
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
